import { track } from '@sloyd/analytics';
import { actions, selectors } from '@sloyd/store-base';
import '@sloyd/types';
import { bridge } from '@sloyd/viewer';
import { delay, fork, put, select, takeEvery } from 'saga-ts';
import { invokeEvent, l } from 'shared-base';
import { generatorParametersToValuesModel } from '../utils/generators';
import { normalizeMaterials } from '../utils/materials';
import { getWackDelta } from '../utils/randomize';
import { customEventChannel } from './channels/channel.customEvent';
import { onSlotHover } from './helpers/blink';
import { dataGuard } from './helpers/guards';

export const saga = {
  id: 'sloyd.materials',
  type: 'entity',
  root: root,
  trigger: {
    actionTypes: ['MATERIALS'],
  },
};

type Verb =
  | 'patch' //
  | 'slotChange'
  | 'selectPalette'
  | 'indexHover'
  | 'fromLibrary'
  | 'reset';

type Action = {
  type: 'MATERIALS';
  verb: Verb;
  id: string;
  payload: Json;
};

const map: Record<Verb, any> = {
  patch: patchMaterial,
  reset: resetMaterial,
  slotChange: slotChange,
  indexHover: indexHover,
  selectPalette: selectPalette,
  fromLibrary: fromLibrary,
};

const analyitcsMapMaterials: any = {
  patch: 'Patch Material',
  reset: 'Reset Material',
  slotChange: 'Slot Change',
  indexHover: 'Index Hover',
  selectPalette: 'Select Palette',
  fromLibrary: 'From Library',
};

export function* indexHover(action: Action, model: IModel) {
  const { payload } = action;
}

export function* fromLibrary(action: Action, model: IModel) {
  try {
    const { payload } = action;
    const { material } = payload;
    const { id } = material;

    const currentIds = yield* select(selectors.raw.$rawCurrentIds);

    const { slotId } = currentIds;

    // TODO: api
    const materialWithTexture = yield* select(selectors.single.$texturedMaterial, id);

    const change = {
      ...materialWithTexture,
      tint: '#ffffff',
    };

    bridge.patchMaterials('main', { [slotId]: change });

    const materials = { ...model.materials };
    materials[slotId] = change;
    yield put(actions.models.patch(model.id, { materials }));
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* slotChange(action: Action, model: IModel) {
  const { payload } = action;
  const { slotId } = payload;

  yield put(actions.currentIds.patch({ slotId }));
}

export function* resetMaterial(action: Action, model: IModel) {
  try {
    const { generatorId, valuesModel } = model;
    const generator = yield* select(selectors.single.$generator, generatorId);
    const defaultWackValues = yield* select(selectors.parameters.$defaultWackValues);

    if (!generator) {
      throw new Error('No generator');
    }

    const { parameters } = generator;

    // only reset wack values if exist
    const extra = getWackDelta(valuesModel, defaultWackValues);
    const values = generatorParametersToValuesModel(parameters, extra);
    yield put(actions.valuesModel.patch(values));

    bridge.patchValuesModel('main', values);
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* patchMaterial(action: Action, model: IModel) {
  try {
    const { payload, id } = action;
    bridge.patchMaterials('main', { [id]: payload });

    const materials = { ...model.materials };
    materials[id] = { ...materials[id], ...payload };
    yield put(actions.models.patch(model.id, { materials }));
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* selectPalette(action: Action, model: IModel) {
  const { payload } = action;
  const { paletteId } = payload;

  if (!model) {
    return;
  }

  const palette = yield* select(selectors.single.$palette, paletteId);

  if (!palette) {
    return;
  }

  const materials = normalizeMaterials(palette.materials, paletteId);

  bridge.patchMaterials('main', materials);

  yield put(actions.models.patch(model.id, { paletteId, materials }));
}

export function* materials(action: any) {
  const { verb, id, origin } = action;
  yield delay(10);

  const saga = (map as any)[verb];

  if (!saga) {
    return;
  }

  l(`saga|saga.materials ${verb} on ${id}`);

  yield* dataGuard();

  const model = yield* select(selectors.single.$currentModel);
  track(analyitcsMapMaterials[verb], { origin, id });

  yield* fork(saga, action, model);
}

export function* root() {
  yield takeEvery('MATERIALS', materials);

  const channel = customEventChannel('slot/hover');
  yield takeEvery(channel, onSlotHover);
}
